import { useCallback, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';

const tagRows: string[][] = [
  ['#Branding', '#Logo'],
  ['#Social_Media', '#Poster'],
  ['#Packaging', '#Illustration'],
  ['#3D_&_Motion_Graphic'],
];

type FallingLetter = {
  ch: string;
  right: string;
  top: string;
  size: string;
  rotEnd: number;
  fallRot: number;
};

const letters: FallingLetter[] = [
  { ch: 'P', right: '46%', top: '38%', size: 'clamp(90px, 11vw, 180px)', rotEnd: -8, fallRot: 280 },
  { ch: 'O', right: '26%', top: '30%', size: 'clamp(110px, 13vw, 210px)', rotEnd: 6, fallRot: -320 },
  { ch: 'R', right: '4%', top: '22%', size: 'clamp(120px, 14vw, 230px)', rotEnd: 14, fallRot: 360 },
  { ch: 'T', right: '58%', top: '52%', size: 'clamp(90px, 11vw, 180px)', rotEnd: -14, fallRot: -260 },
  { ch: 'F', right: '44%', top: '58%', size: 'clamp(90px, 11vw, 180px)', rotEnd: -4, fallRot: 300 },
  { ch: 'O', right: '28%', top: '54%', size: 'clamp(110px, 13vw, 210px)', rotEnd: 10, fallRot: -240 },
  { ch: 'L', right: '10%', top: '52%', size: 'clamp(100px, 12vw, 200px)', rotEnd: 18, fallRot: 340 },
  { ch: 'I', right: '54%', top: '72%', size: 'clamp(80px, 10vw, 160px)', rotEnd: -18, fallRot: -300 },
  { ch: 'O', right: '34%', top: '74%', size: 'clamp(110px, 13vw, 210px)', rotEnd: 4, fallRot: 260 },
];

export default function Home() {
  const [searchOpen, setSearchOpen] = useState(false);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const slotRefs = useRef<Array<HTMLSpanElement | null>>([]);
  const fallingRef = useRef<Set<number>>(new Set());
  const burstLayerRef = useRef<HTMLDivElement | null>(null);

  // Magnetic attraction: all letters lean toward the mouse.
  // Nearer letters react stronger, so the group moves together like curious creatures.
  const handleStageMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    const el = stageRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    slotRefs.current.forEach((slot, idx) => {
      if (!slot) return;
      if (fallingRef.current.has(idx)) return;
      const sr = slot.getBoundingClientRect();
      const cx = sr.left + sr.width / 2 - rect.left;
      const cy = sr.top + sr.height / 2 - rect.top;
      const dx = mx - cx;
      const dy = my - cy;
      const dist = Math.hypot(dx, dy);
      const strength = Math.max(0, 1 - dist / 380);
      const tx = dx * 0.16 * strength;
      const ty = dy * 0.16 * strength;
      const tilt = (dx / 45) * strength;
      slot.style.setProperty('--tx', `${tx.toFixed(1)}px`);
      slot.style.setProperty('--ty', `${ty.toFixed(1)}px`);
      slot.style.setProperty('--tilt', `${tilt.toFixed(2)}deg`);
    });
  };

  const handleStageLeave = () => {
    slotRefs.current.forEach((slot, idx) => {
      if (!slot) return;
      if (fallingRef.current.has(idx)) return;
      slot.style.setProperty('--tx', '0px');
      slot.style.setProperty('--ty', '0px');
      slot.style.setProperty('--tilt', '0deg');
    });
  };

  // Spawn a burst of small square particles at (x, y) inside the stage.
  const spawnBurst = useCallback((x: number, y: number) => {
    const layer = burstLayerRef.current;
    if (!layer) return;

    // Central flash ring
    const flash = document.createElement('div');
    flash.className = 'burst-flash';
    flash.style.left = `${x}px`;
    flash.style.top = `${y}px`;
    layer.appendChild(flash);
    window.setTimeout(() => flash.remove(), 550);

    // Radial shards
    const count = 14;
    for (let i = 0; i < count; i += 1) {
      const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.35;
      const distance = 90 + Math.random() * 90;
      const px = Math.cos(angle) * distance;
      const py = Math.sin(angle) * distance;
      const rot = (Math.random() - 0.5) * 720;
      const size = 6 + Math.random() * 8;

      const p = document.createElement('div');
      p.className = 'burst-particle';
      p.style.left = `${x}px`;
      p.style.top = `${y}px`;
      p.style.width = `${size}px`;
      p.style.height = `${size}px`;
      p.style.setProperty('--px', `${px.toFixed(1)}px`);
      p.style.setProperty('--py', `${py.toFixed(1)}px`);
      p.style.setProperty('--pr', `${rot.toFixed(1)}deg`);
      p.style.animationDelay = `${(Math.random() * 0.06).toFixed(3)}s`;
      layer.appendChild(p);
      window.setTimeout(() => p.remove(), 1050);
    }
  }, []);

  // Click → the letter loses its weight, floats up briefly (zero-g),
  // then plummets off the screen. After it's gone, it silently comes back.
  const triggerFall = useCallback(
    (index: number, e: ReactMouseEvent<HTMLSpanElement>) => {
      if (fallingRef.current.has(index)) return;
      fallingRef.current.add(index);

      const target = slotRefs.current[index];
      const stage = stageRef.current;
      if (!target || !stage) return;

      // Spawn burst at click point (relative to stage)
      const stageRect = stage.getBoundingClientRect();
      spawnBurst(e.clientX - stageRect.left, e.clientY - stageRect.top);

      // Freeze the current magnetic offset as the animation's starting point
      void target.offsetWidth;
      target.classList.add('is-falling');

      // After fall: hide instantly, reset offsets, then quietly fade back in place.
      window.setTimeout(() => {
        target.classList.remove('is-falling');
        target.classList.add('is-gone');
        target.style.setProperty('--tx', '0px');
        target.style.setProperty('--ty', '0px');
        target.style.setProperty('--tilt', '0deg');
        void target.offsetWidth;

        window.setTimeout(() => {
          target.style.transition = 'opacity 0.5s ease-out';
          target.classList.remove('is-gone');
          window.setTimeout(() => {
            target.style.transition = '';
            fallingRef.current.delete(index);
          }, 520);
        }, 380);
      }, 2200);
    },
    [spawnBurst]
  );

  return (
    <div className="min-h-screen bg-background-50 text-foreground-950 font-body flex flex-col">
      {/* Top Nav */}
      <header className="w-full border-b border-foreground-950/15">
        <nav className="w-full flex items-center justify-between px-6 md:px-10 py-4 md:py-5">
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-foreground-950 text-background-50 flex items-center justify-center">
              <i className="ri-user-smile-line text-lg md:text-xl"></i>
            </div>
            <span className="font-heading text-lg md:text-xl tracking-tight">
              Qorry&rsquo;aina D. J.
            </span>
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            <span className="hidden sm:inline font-body text-sm md:text-base text-foreground-800">
              Graphic Design Portfolio
            </span>
            <button
              type="button"
              onClick={() => setSearchOpen((s) => !s)}
              aria-label="Search"
              className="w-9 h-9 md:w-10 md:h-10 rounded-full border border-foreground-950/70 flex items-center justify-center hover:bg-foreground-950 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-search-line text-base md:text-lg"></i>
            </button>
          </div>
        </nav>

        {searchOpen && (
          <div className="w-full border-t border-foreground-950/10 bg-background-50 px-6 md:px-10 py-3 flex items-center gap-3">
            <i className="ri-search-line text-foreground-700"></i>
            <input
              type="text"
              placeholder="Search projects, tags, categories..."
              className="w-full bg-transparent outline-none text-sm md:text-base placeholder:text-foreground-500"
            />
            <button
              type="button"
              onClick={() => setSearchOpen(false)}
              aria-label="Close search"
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-foreground-950/10 cursor-pointer"
            >
              <i className="ri-close-line"></i>
            </button>
          </div>
        )}
      </header>

      {/* Hero */}
      <main className="flex-1 w-full px-6 md:px-10 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.8fr)] gap-8 lg:gap-14 items-stretch">
          {/* Left column */}
          <aside className="flex flex-col justify-end min-h-[420px] lg:min-h-[560px] pl-2 md:pl-6">
            <div className="mb-10 md:mb-14 flex">
              <i
                className="ri-arrow-right-down-line text-foreground-950 leading-none"
                style={{ fontSize: 'clamp(120px, 14vw, 200px)', fontWeight: 900 }}
              ></i>
            </div>

            <ul className="font-mono text-sm md:text-base text-foreground-950">
              {tagRows.map((row, idx) => (
                <li
                  key={idx}
                  className={`flex flex-wrap items-center gap-x-6 md:gap-x-8 gap-y-1 py-3 md:py-4 border-t border-foreground-950/60 ${
                    idx === tagRows.length - 1 ? 'border-b' : ''
                  }`}
                >
                  {row.map((tag) => (
                    <span key={tag} className="whitespace-nowrap tracking-tight">
                      {tag}
                    </span>
                  ))}
                </li>
              ))}
            </ul>
          </aside>

          {/* Right: blue square */}
          <section
            ref={stageRef}
            onMouseMove={handleStageMove}
            onMouseLeave={handleStageLeave}
            className="relative w-full aspect-square bg-primary-500 overflow-hidden select-none"
            aria-label="Portfolio showcase"
          >
            {/* 2026 */}
            <div
              className="absolute left-6 md:left-10 bottom-6 md:bottom-10 font-heading text-background-50 leading-none"
              style={{
                fontSize: 'clamp(48px, 6vw, 88px)',
                letterSpacing: '-0.03em',
              }}
            >
              2026
            </div>

            <div
              className="absolute bottom-8 md:bottom-14 left-[calc(6rem+6vw)] hidden md:block h-2 w-12 md:w-20 bg-background-50"
            ></div>

            {/* Burst particle layer — sits above letters, receives no pointer events */}
            <div ref={burstLayerRef} className="burst-layer"></div>

            {/* PORTFOLIO letters — magnetic follow + hover awaken + click to free-fall */}
            <div className="absolute inset-0">
              {letters.map((L, i) => {
                const slotStyle: CSSProperties & Record<string, string | number> = {
                  right: L.right,
                  top: L.top,
                  '--fall-rot': `${L.fallRot}deg`,
                };
                const letterStyle: CSSProperties & Record<string, string | number> = {
                  fontSize: L.size,
                  '--rot-end': `${L.rotEnd}deg`,
                };
                return (
                  <span
                    key={i}
                    ref={(node) => {
                      slotRefs.current[i] = node;
                    }}
                    className="letter-slot"
                    style={slotStyle}
                    onClick={(e) => triggerFall(i, e)}
                  >
                    <span
                      className="font-heading text-background-50 leading-none letter-fall block"
                      style={letterStyle}
                    >
                      {L.ch}
                    </span>
                  </span>
                );
              })}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}